import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    CalendarDays,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Clock3,
    Info,
    Loader2,
    TrendingUp,
    UserRound,
    WalletCards,
} from "lucide-react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import {
    getEmployeeTimeoffOverview,
    getEmployeeTimeoffRequestsList,
    getManagerTimeoffEmployees,
    resetMobileTimeoffCategoryDetail,
    setSelectedTimeoffForView,
} from "../../../redux/features/timeoff";
import { getAssignedTimeoffCategories } from "../../../redux/features/common";
import { RootState, useAppDispatch } from "../../../redux/store";
import { clearGlobalBackHandler, setGlobalBackHandler } from "../../../hooks";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { DateTimeUtil, formatHoursRounded } from "../../../utils";
import { Timeoff, TimeoffCategoryType, TimeoffPaginationMeta } from "../../../types";
import { getApprovalStatusText } from "../../../utils/timeoff";
import { TimeoffApprovalStatusEnum } from "../../../enums";

type StatusFilter = "ALL" | TimeoffApprovalStatusEnum;

type LocationState = {
    from?: string;
    selectedAssignmentKey?: string;
    selectedEmployeeId?: string;
    selectedStatus?: StatusFilter;
    selectedYear?: number;
};

type AssignmentOption = TimeoffCategoryType & {
    assignmentKey: string;
};

type SelectOption = {
    label: string;
    subtitle?: string;
    value: string;
};

const PAGE_LIMIT = 20;
const CURRENT_YEAR = new Date().getFullYear();

const buildAssignmentKey = (category: Pick<TimeoffCategoryType, "category_code" | "policy_code">): string =>
    `${category.category_code || ""}::${category.policy_code || ""}`;

const getYearRange = (): number[] => Array.from({ length: 6 }, (_, index) => CURRENT_YEAR - index);

const toNumber = (value?: string | number | null): number => {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
};

const getYearBounds = (year: number) => ({
    start_date: `${year}-01-01`,
    end_date: `${year}-12-31`,
});

const formatDate = (date?: string): string => (date ? DateTimeUtil.formatDate(date, "MMM d, yyyy") : "Unknown date");

const formatRequestDate = (request: Timeoff): string => {
    if (!request.start_date || !request.end_date) return "No date";
    return DateTimeUtil.formatDateRangeFancy(request.start_date, request.end_date);
};

const formatRequestDayLabel = (request: Timeoff): string => {
    if (!request.start_date) return "-";
    if (!request.end_date || request.start_date === request.end_date) {
        return DateTimeUtil.formatDate(request.start_date, "d");
    }

    const startMonth = DateTimeUtil.formatDate(request.start_date, "MMM");
    const endMonth = DateTimeUtil.formatDate(request.end_date, "MMM");
    const startDay = DateTimeUtil.formatDate(request.start_date, "d");
    const endDay = DateTimeUtil.formatDate(request.end_date, "d");

    if (startMonth === endMonth) {
        return `${startDay}-${endDay}`;
    }

    return `${startDay} to ${endDay}`;
};

const formatRequestWeekdayLabel = (request: Timeoff): string => {
    if (!request.start_date) return "";
    if (!request.end_date || request.start_date === request.end_date) {
        return DateTimeUtil.formatDate(request.start_date, "EEE");
    }

    const startWeekday = DateTimeUtil.formatDate(request.start_date, "EEE");
    const endWeekday = DateTimeUtil.formatDate(request.end_date, "EEE");

    return `${startWeekday}-${endWeekday}`;
};

const formatStatus = (status?: string): string => {
    if (!status) return "Pending";
    return status.toLowerCase().replace(/_/g, " ");
};

const selectStyles = {
    control: (baseStyles: any, state: any) => ({
        ...baseStyles,
        backgroundColor: "white",
        borderColor: state.isFocused ? "#60a5fa" : "#e2e8f0",
        borderRadius: "1rem",
        boxShadow: "none",
        minHeight: 64,
        paddingLeft: 4,
        ":hover": {
            borderColor: state.isFocused ? "#60a5fa" : "#cbd5e1",
        },
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
    menuPortal: (baseStyles: any) => ({
        ...baseStyles,
        zIndex: 9999,
    }),
    menu: (baseStyles: any) => ({
        ...baseStyles,
        border: "1px solid #e2e8f0",
        borderRadius: "1rem",
        overflow: "hidden",
    }),
    option: (baseStyles: any, state: any) => ({
        ...baseStyles,
        backgroundColor: state.isSelected ? "#2563eb" : state.isFocused ? "#eff6ff" : "white",
        color: state.isSelected ? "white" : "#0f172a",
        cursor: "pointer",
    }),
    placeholder: (baseStyles: any) => ({
        ...baseStyles,
        color: "#94a3b8",
    }),
    valueContainer: (baseStyles: any) => ({
        ...baseStyles,
        paddingLeft: 0,
    }),
} as const;

const ManagerTimeoffList: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { setSourcePath } = useNavigationHistory();
    const locationState = (location.state || {}) as LocationState;

    const { assignedTimeoffCategories, isLoadingAssignedCategories } = useSelector((state: RootState) => state.common);
    const {
        isFetchingManagerTimeoffEmployees,
        managerTimeoffEmployeeRecords,
        isLoadingMobileCategoryRequests,
        isLoadingMoreMobileCategoryRequests,
        isLoadingMobileCategoryOverview,
        mobileCategoryRequests,
        mobileCategoryRequestsMeta,
        mobileCategoryOverviewError,
        mobileCategoryRequestsError,
        mobileTimeoffCategory,
        mobileTimeoffCategoryBalance,
        mobileTimeoffCategorySummary,
    } = useSelector((state: RootState) => state.timeoff);

    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>(locationState.selectedEmployeeId || "");
    const [selectedAssignmentKey, setSelectedAssignmentKey] = useState<string>(locationState.selectedAssignmentKey || "");
    const [selectedStatus, setSelectedStatus] = useState<StatusFilter>(locationState.selectedStatus || "ALL");
    const [selectedYear, setSelectedYear] = useState<number>(locationState.selectedYear || CURRENT_YEAR);

    const employees = useMemo(() => managerTimeoffEmployeeRecords || [], [managerTimeoffEmployeeRecords]);
    const selectedEmployee = employees.find((employee) => employee.emp_detail?.employee_id === selectedEmployeeId);
    const selectedEmployeeName = selectedEmployee?.emp_detail?.emp_name || "";
    const yearOptions = useMemo(getYearRange, []);
    const employeeOptions = useMemo<SelectOption[]>(
        () =>
            employees.map((employee) => ({
                label: employee.emp_detail?.emp_name || employee.emp_detail?.employee_id || "",
                subtitle: employee.department?.department_name || employee.department?.name || undefined,
                value: employee.emp_detail?.employee_id || "",
            })),
        [employees]
    );

    const assignmentOptions = useMemo<AssignmentOption[]>(() => {
        const optionsByKey = new Map<string, AssignmentOption>();

        assignedTimeoffCategories.forEach((category) => {
            if (!category.category_code) return;
            const assignmentKey = buildAssignmentKey(category);
            if (!optionsByKey.has(assignmentKey)) {
                optionsByKey.set(assignmentKey, { ...category, assignmentKey });
            }
        });

        return Array.from(optionsByKey.values());
    }, [assignedTimeoffCategories]);
    const categorySelectOptions = useMemo<SelectOption[]>(
        () =>
            assignmentOptions.map((option) => ({
                label: option.category_name || option.category_code || "",
                subtitle: option.policy_code || undefined,
                value: option.assignmentKey,
            })),
        [assignmentOptions]
    );
    const yearSelectOptions = useMemo<SelectOption[]>(
        () =>
            yearOptions.map((year) => ({
                label: String(year),
                value: String(year),
            })),
        [yearOptions]
    );
    const statusOptions = useMemo<SelectOption[]>(
        () => [
            { label: "All Status", value: "ALL" },
            { label: "Pending", value: TimeoffApprovalStatusEnum.PENDING },
            { label: "Approved", value: TimeoffApprovalStatusEnum.APPROVED },
            { label: "Rejected", value: TimeoffApprovalStatusEnum.REJECTED },
            { label: "Cancelled", value: TimeoffApprovalStatusEnum.CANCELLED },
        ],
        []
    );

    const selectedAssignment = assignmentOptions.find((option) => option.assignmentKey === selectedAssignmentKey);
    const category = mobileTimeoffCategory || selectedAssignment;
    const balance = mobileTimeoffCategoryBalance || {
        amount: selectedAssignment?.time_off_hours_balance,
        unit: selectedAssignment?.track_time_in,
        last_update: undefined,
    };
    const unit = balance.unit || category?.track_time_in || "Hours";
    const hasSelectedCategory = Boolean(selectedAssignment?.category_code);

    const summary = {
        accruedYtd: mobileTimeoffCategorySummary?.accrued_ytd,
        currentBalance: balance.amount,
        pendingRequests: mobileTimeoffCategorySummary?.pending_requests,
        usedThisYear: mobileTimeoffCategorySummary?.used_this_year,
    };

    useEffect(() => {
        dispatch(setTopNavNameTitle("Team Time Off"));
        setSourcePath(locationState.from || "/mobile-dashboard");
        setGlobalBackHandler(() => {
            navigate("/mobile-dashboard");
            return true;
        });

        return () => {
            clearGlobalBackHandler();
        };
    }, [dispatch, locationState.from, navigate, setSourcePath]);

    useEffect(() => {
        dispatch(
            getManagerTimeoffEmployees({
                employee_id: [],
                department_code: [],
                group_code: [],
                site_code: [],
                fields: ["total_hours"],
            })
        );
    }, [dispatch]);

    useEffect(() => {
        if (!employees.length) return;

        const employeeStillAvailable = employees.some((employee) => employee.emp_detail?.employee_id === selectedEmployeeId);
        if (!selectedEmployeeId || !employeeStillAvailable) {
            setSelectedEmployeeId(employees[0].emp_detail?.employee_id || "");
        }
    }, [employees, selectedEmployeeId]);

    useEffect(() => {
        if (!selectedEmployeeId) return;

        dispatch(resetMobileTimeoffCategoryDetail());
        dispatch(getAssignedTimeoffCategories({ employee_id: selectedEmployeeId }));
    }, [dispatch, selectedEmployeeId]);

    useEffect(() => {
        if (!assignmentOptions.length) {
            setSelectedAssignmentKey("");
            return;
        }

        const assignmentStillAvailable = assignmentOptions.some(
            (categoryOption) => categoryOption.assignmentKey === selectedAssignmentKey
        );
        if (!selectedAssignmentKey || !assignmentStillAvailable) {
            setSelectedAssignmentKey(assignmentOptions[0].assignmentKey);
        }
    }, [assignmentOptions, selectedAssignmentKey]);

    const buildCategoryPayload = React.useCallback(
        (page: number = 1, limit: number = PAGE_LIMIT) => {
            if (!selectedEmployeeId || !selectedAssignment?.category_code) return undefined;

            return {
                category_code: selectedAssignment.category_code,
                policy_code: selectedAssignment.policy_code || undefined,
                employee_id: selectedEmployeeId,
                page,
                limit,
                ...getYearBounds(selectedYear),
            };
        },
        [selectedAssignment?.category_code, selectedAssignment?.policy_code, selectedEmployeeId, selectedYear]
    );

    const loadRequests = React.useCallback(
        (page: number = 1, limit: number = PAGE_LIMIT) => {
            const payload = buildCategoryPayload(page, limit);
            if (!payload) return;

            dispatch(
                getEmployeeTimeoffRequestsList({
                    ...payload,
                    status: selectedStatus === "ALL" ? undefined : selectedStatus,
                })
            );
        },
        [buildCategoryPayload, dispatch, selectedStatus]
    );

    useEffect(() => {
        const payload = buildCategoryPayload(1, PAGE_LIMIT);
        if (!payload) return;

        dispatch(
            getEmployeeTimeoffOverview({
                category_code: payload.category_code,
                policy_code: payload.policy_code,
                employee_id: payload.employee_id,
                start_date: payload.start_date,
                end_date: payload.end_date,
            })
        );
    }, [buildCategoryPayload, dispatch]);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    const handleEmployeeChange = (option: SelectOption | null) => {
        setSelectedEmployeeId(option?.value || "");
        setSelectedAssignmentKey("");
        setSelectedStatus("ALL");
        dispatch(resetMobileTimeoffCategoryDetail());
    };

    const handleCategoryChange = (option: SelectOption | null) => {
        setSelectedAssignmentKey(option?.value || "");
        setSelectedStatus("ALL");
        dispatch(resetMobileTimeoffCategoryDetail());
    };

    const handleYearChange = (option: SelectOption | null) => {
        setSelectedYear(Number(option?.value || CURRENT_YEAR));
        dispatch(resetMobileTimeoffCategoryDetail());
    };

    const handleStatusChange = (option: SelectOption | null) => {
        setSelectedStatus((option?.value as StatusFilter) || "ALL");
    };

    const loadMore = () => {
        if (!mobileCategoryRequestsMeta?.has_more) return;

        loadRequests(
            Number(mobileCategoryRequestsMeta.page ?? 1) + 1,
            Number(mobileCategoryRequestsMeta.limit ?? PAGE_LIMIT)
        );
    };

    const openRequest = (request: Timeoff) => {
        dispatch(setSelectedTimeoffForView(request));
        navigate("/m-timeoff-details", {
            state: {
                from: "/m-manager-timeoff",
                fromState: {
                    selectedAssignmentKey,
                    selectedEmployeeId,
                    selectedStatus,
                    selectedYear,
                },
                category,
                employeeId: selectedEmployeeId,
                employeeName: selectedEmployeeName,
                readOnly: true,
            },
        });
    };

    return (
        <div className="min-h-full bg-[#f6f8fc] px-3 py-4 text-slate-900 sm:px-5 lg:px-8">
            <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
                <header>
                    <h1 className="text-xl font-bold tracking-tight text-slate-950 sm:text-2xl">
                        Employee Time-Off Balance
                    </h1>
                    <p className="mt-0.5 text-sm text-slate-500">
                        View balances and requests by employee, category and year.
                    </p>
                </header>

                <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    <SelectFilterCard
                        label="Employee"
                        loading={isFetchingManagerTimeoffEmployees}
                        disabled={isFetchingManagerTimeoffEmployees || !employees.length}
                        value={employeeOptions.find((option) => option.value === selectedEmployeeId) || null}
                        onChange={handleEmployeeChange}
                        leadIcon={
                            <EmployeeAvatar
                                name={selectedEmployeeName}
                                url={selectedEmployee?.emp_detail?.profile_image_url}
                            />
                        }
                        displayValue={selectedEmployeeName || "Select employee"}
                        options={employeeOptions}
                        placeholder="Select employee"
                    />

                    <SelectFilterCard
                        label="Time-Off Category"
                        loading={isLoadingAssignedCategories && !assignmentOptions.length}
                        disabled={!selectedEmployeeId || isLoadingAssignedCategories || !assignmentOptions.length}
                        value={categorySelectOptions.find((option) => option.value === selectedAssignmentKey) || null}
                        onChange={handleCategoryChange}
                        leadIcon={
                            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                                <CalendarDays className="h-5 w-5" />
                            </span>
                        }
                        displayValue={
                            selectedAssignment?.category_name || selectedAssignment?.category_code || "Select category"
                        }
                        options={categorySelectOptions}
                        placeholder="Select category"
                    />

                    <SelectFilterCard
                        label="Year"
                        value={yearSelectOptions.find((option) => option.value === String(selectedYear)) || null}
                        onChange={handleYearChange}
                        leadIcon={
                            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                                <CalendarDays className="h-5 w-5" />
                            </span>
                        }
                        displayValue={String(selectedYear)}
                        options={yearSelectOptions}
                        placeholder="Select year"
                    />
                </section>

                <section className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                    <MetricCard
                        accent="blue"
                        icon={<WalletCards className="h-6 w-6" />}
                        label="Current Balance"
                        unit={unit}
                        value={formatHoursRounded(summary.currentBalance)}
                    />
                    <MetricCard
                        accent="green"
                        icon={<TrendingUp className="h-6 w-6" />}
                        label="Used This Year"
                        unit={unit}
                        value={formatHoursRounded(summary.usedThisYear)}
                    />
                    <MetricCard
                        accent="amber"
                        icon={<Clock3 className="h-6 w-6" />}
                        label="Pending Requests"
                        unit="Requests"
                        value={String(toNumber(summary.pendingRequests))}
                    />
                    <MetricCard
                        accent="violet"
                        icon={<CalendarDays className="h-6 w-6" />}
                        label="Accrued YTD"
                        unit={unit}
                        value={formatHoursRounded(summary.accruedYtd)}
                    />
                </section>

                <section className="rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-4">
                            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-3xl bg-emerald-50">
                                <CalendarDays className="h-8 w-8 text-emerald-600" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="truncate text-2xl font-bold text-slate-950">
                                    {category?.category_name || "Select a category"}
                                </h2>
                                <p className="mt-1 text-sm font-medium text-slate-500">Tracked in {unit}</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-4 text-sm md:min-w-[360px] md:border-l md:border-t-0 md:pl-5 md:pt-0">
                            <SummaryPair label="Employee" value={selectedEmployeeName || "-"} />
                            <SummaryPair label="Policy" value={category?.policy_code || "Assigned policy"} />
                            <SummaryPair
                                label="Last Updated"
                                value={balance.last_update ? formatDate(balance.last_update) : "Newest first"}
                            />
                            <SummaryPair label="Year" value={String(selectedYear)} />
                        </div>
                    </div>
                </section>

                <section className="overflow-hidden rounded-[1.35rem] border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 p-3">
                        <div className="sm:max-w-[240px]">
                            <Select
                                classNamePrefix="react-select"
                                components={{ IndicatorSeparator: () => null }}
                                isSearchable
                                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                                options={statusOptions}
                                placeholder="All Status"
                                styles={selectStyles}
                                value={statusOptions.find((option) => option.value === selectedStatus) || null}
                                onChange={(option) => handleStatusChange(option as SelectOption | null)}
                            />
                        </div>
                    </div>

                    {!hasSelectedCategory ? (
                        <EmptyState
                            icon={<CalendarDays className="h-6 w-6" />}
                            title="Select an employee and category"
                            message="Time-off requests will appear once a category is selected."
                        />
                    ) : isLoadingMobileCategoryOverview || isLoadingMobileCategoryRequests ? (
                        <div className="flex min-h-[280px] items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        </div>
                    ) : mobileCategoryOverviewError || mobileCategoryRequestsError ? (
                        <div className="p-6 text-center">
                            <p className="font-semibold text-slate-900">
                                {mobileCategoryOverviewError || mobileCategoryRequestsError}
                            </p>
                            <button
                                className="mt-4 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
                                onClick={() => {
                                    const payload = buildCategoryPayload(1, PAGE_LIMIT);
                                    if (payload) {
                                        dispatch(
                                            getEmployeeTimeoffOverview({
                                                category_code: payload.category_code,
                                                policy_code: payload.policy_code,
                                                employee_id: payload.employee_id,
                                                start_date: payload.start_date,
                                                end_date: payload.end_date,
                                            })
                                        );
                                    }
                                    loadRequests();
                                }}
                            >
                                Try again
                            </button>
                        </div>
                    ) : (
                        <RequestsPanel
                            meta={mobileCategoryRequestsMeta}
                            records={mobileCategoryRequests}
                            onOpenRequest={openRequest}
                            unit={unit}
                        />
                    )}

                    {!isLoadingMobileCategoryOverview &&
                        !isLoadingMobileCategoryRequests &&
                        !mobileCategoryOverviewError &&
                        !mobileCategoryRequestsError &&
                        mobileCategoryRequestsMeta?.has_more && (
                        <div className="border-t border-slate-100 p-3">
                            <button
                                className="flex w-full items-center justify-center rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 disabled:opacity-60"
                                disabled={isLoadingMoreMobileCategoryRequests}
                                onClick={loadMore}
                            >
                                {isLoadingMoreMobileCategoryRequests ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : null}
                                Load more
                            </button>
                        </div>
                    )}
                </section>

                <div className="mb-6 flex gap-2 rounded-2xl bg-blue-50 px-4 py-3 text-xs font-medium text-blue-700 sm:text-sm">
                    <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <p>All times are shown in {unit.toLowerCase()}. Balances are based on approved requests and company policy.</p>
                </div>
            </div>
        </div>
    );
};

const SelectFilterCard = ({
    disabled,
    displayValue,
    label,
    leadIcon,
    loading,
    options,
    onChange,
    placeholder,
    value,
}: {
    disabled?: boolean;
    displayValue: string;
    label: string;
    leadIcon: React.ReactNode;
    loading?: boolean;
    onChange: (option: SelectOption | null) => void;
    options: SelectOption[];
    placeholder: string;
    value: SelectOption | null;
}) => (
    <div>
        <span className="mb-1.5 block text-xs font-semibold text-slate-500">{label}</span>
        <div className="relative flex min-h-[64px] items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition focus-within:border-blue-400">
            <span className="pointer-events-none flex-shrink-0">
                {loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : leadIcon}
            </span>
            <div className="pointer-events-none min-w-0 flex-1">
                <p className={`truncate text-sm font-bold ${disabled && !loading ? "text-slate-400" : "text-slate-800"}`}>
                    {displayValue}
                </p>
            </div>
            <div className="absolute inset-0">
                <Select
                    className="h-full opacity-0"
                    classNamePrefix="react-select"
                    components={{ IndicatorSeparator: () => null }}
                    formatOptionLabel={(option: SelectOption) => (
                        <div className="min-w-0">
                            <div className="truncate text-sm font-medium">{option.label}</div>
                            {option.subtitle ? (
                                <div className="truncate text-xs text-slate-400">{option.subtitle}</div>
                            ) : null}
                        </div>
                    )}
                    isSearchable
                    isDisabled={disabled || loading}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                    options={options}
                    placeholder={placeholder}
                    styles={selectStyles}
                    value={value}
                    onChange={(option) => onChange(option as SelectOption | null)}
                />
            </div>
            <ChevronDown className="pointer-events-none h-4 w-4 flex-shrink-0 text-slate-400" />
        </div>
    </div>
);

const EmployeeAvatar = ({ name, url }: { name: string; url?: string }) => {
    if (url) {
        return <img alt={name} className="h-10 w-10 flex-shrink-0 rounded-full object-cover" src={url} />;
    }

    return (
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <UserRound className="h-5 w-5" />
        </span>
    );
};

const MetricCard = ({
    accent,
    icon,
    label,
    unit,
    value,
}: {
    accent: "amber" | "blue" | "green" | "violet";
    icon: React.ReactNode;
    label: string;
    unit: string;
    value: string;
}) => {
    const styles = {
        amber: "from-amber-50 to-orange-50 text-amber-600",
        blue: "from-blue-50 to-sky-50 text-blue-600",
        green: "from-emerald-50 to-green-50 text-emerald-600",
        violet: "from-violet-50 to-fuchsia-50 text-violet-700",
    };

    return (
        <article className={`rounded-[1.35rem] border border-slate-200 bg-gradient-to-br ${styles[accent]} p-4 shadow-sm sm:p-5`}>
            <div className="flex items-start gap-3">
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white/70">
                    {icon}
                </span>
                <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-700">{label}</p>
                    <p className="mt-2 text-2xl font-black leading-none sm:text-3xl">{value}</p>
                    <p className="mt-1 text-xs font-semibold text-slate-500">{unit}</p>
                </div>
            </div>
        </article>
    );
};

const SummaryPair = ({ label, value }: { label: string; value: string }) => (
    <div className="min-w-0">
        <p className="text-xs font-semibold text-slate-400">{label}</p>
        <p className="mt-1 truncate font-bold text-slate-800">{value}</p>
    </div>
);

const RequestsPanel = ({
    meta,
    records,
    onOpenRequest,
    unit,
}: {
    meta?: TimeoffPaginationMeta;
    records: Timeoff[];
    onOpenRequest: (request: Timeoff) => void;
    unit: string;
}) => {
    if (!records.length) {
        return (
            <EmptyState
                icon={<CalendarDays className="h-6 w-6" />}
                title="No requests found"
                message="There are no time-off requests for this selection."
            />
        );
    }

    return (
        <div>
            <div className="hidden grid-cols-[110px_minmax(0,1fr)_150px_100px_48px] border-b border-slate-100 px-5 py-3 text-xs font-bold text-slate-500 md:grid">
                <span>Date</span>
                <span>Request Details</span>
                <span>Status</span>
                <span>Duration</span>
                <span />
            </div>
            <div className="divide-y divide-slate-100">
                {records.map((request, index) => (
                    <button
                        key={`${request.time_off_request_id}-${index}`}
                        className="relative flex w-full items-start gap-3 px-4 py-4 text-left transition hover:bg-slate-50 active:bg-slate-50 md:grid md:grid-cols-[110px_minmax(0,1fr)_150px_100px_32px] md:items-center md:px-5"
                        onClick={() => onOpenRequest(request)}
                    >
                        <DateChip request={request} />
                        <div className="min-w-0 flex-1 pr-8">
                            <div className="md:hidden">
                                <p className="line-clamp-2 text-sm font-bold leading-5 text-slate-950">
                                    {request.reason || request.category_name || "Time-off request"}
                                </p>
                                <p className="mt-1 text-xs font-medium text-slate-500">
                                    Requested {request.created_date_time ? formatDate(request.created_date_time) : formatRequestDate(request)}
                                </p>
                                <p className="mt-0.5 truncate text-xs text-slate-400">
                                    Request ID: {request.time_off_request_id || "-"}
                                </p>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <StatusBadge status={request.status} />
                                    <span className="text-xs font-semibold text-slate-500">
                                        {formatHoursRounded(request.hours_requested)} {unit}
                                    </span>
                                </div>
                            </div>

                            <div className="hidden md:block">
                                <p className="truncate text-sm font-bold text-slate-950">
                                    {request.reason || request.category_name || "Time-off request"}
                                </p>
                                <p className="mt-1 text-xs font-medium text-slate-500">
                                    Requested {request.created_date_time ? formatDate(request.created_date_time) : formatRequestDate(request)}
                                </p>
                                <p className="mt-0.5 truncate text-xs text-slate-400">
                                    Request ID: {request.time_off_request_id || "-"}
                                </p>
                            </div>
                        </div>
                        <div className="hidden md:block">
                            <StatusBadge status={request.status} />
                        </div>
                        <p className="hidden text-sm font-bold text-slate-700 md:block">
                            {formatHoursRounded(request.hours_requested)} {unit}
                        </p>
                        <ChevronRight className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 md:static md:translate-y-0" />
                    </button>
                ))}
            </div>
            <div className="border-t border-slate-100 px-4 py-3 text-sm font-medium text-slate-500">
                Showing {records.length} of {meta?.count ?? records.length} requests
            </div>
        </div>
    );
};

const DateChip = ({ request }: { request: Timeoff }) => {
    const start = request.start_date ? new Date(request.start_date) : undefined;
    const end = request.end_date ? new Date(request.end_date) : undefined;
    const sameDay = start && end && start.toDateString() === end.toDateString();
    const monthLabel = request.start_date ? DateTimeUtil.formatDate(request.start_date, "MMM") : "-";
    const dayLabel = formatRequestDayLabel(request);
    const weekdayLabel = formatRequestWeekdayLabel(request);

    return (
        <div className="flex min-h-[68px] w-[58px] flex-shrink-0 flex-col items-center justify-center rounded-[20px] bg-slate-50 px-1 py-1.5 text-center md:min-h-[76px] md:w-20">
            <span className="text-[9px] font-black uppercase tracking-[0.12em] text-slate-500">
                {monthLabel}
            </span>
            <span
                className={`mt-0.5 whitespace-nowrap font-black leading-none tracking-tight text-slate-900 ${
                    sameDay ? "text-[1.35rem] md:text-xl" : "text-[1.1rem] md:text-lg"
                }`}
            >
                {dayLabel}
            </span>
            <span className="mt-1 whitespace-nowrap text-[10px] font-semibold leading-none text-slate-400">
                {weekdayLabel}
            </span>
        </div>
    );
};

const StatusBadge = ({ status }: { status?: string }) => {
    const statusClass = getApprovalStatusText(status);
    const badgeClass =
        status === TimeoffApprovalStatusEnum.APPROVED
            ? "bg-emerald-50 ring-emerald-100"
            : status === TimeoffApprovalStatusEnum.REJECTED || status === TimeoffApprovalStatusEnum.CANCELLED
            ? "bg-red-50 ring-red-100"
            : "bg-amber-50 ring-amber-100";

    return (
        <span className={`inline-flex h-fit w-fit items-center rounded-lg px-2.5 py-1 text-xs font-bold capitalize ring-1 ${statusClass} ${badgeClass}`}>
            {status === TimeoffApprovalStatusEnum.APPROVED ? <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> : null}
            {formatStatus(status)}
        </span>
    );
};

const EmptyState = ({ icon, title, message }: { icon: React.ReactNode; title: string; message: string }) => (
    <div className="p-8 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
            {icon}
        </div>
        <p className="mt-4 font-bold text-slate-900">{title}</p>
        <p className="mt-1 text-sm text-slate-500">{message}</p>
    </div>
);

export default ManagerTimeoffList;
